import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, IdCard } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { membershipCardsService } from '../services/membershipCards';
import type { MembershipCard } from '../types/membership';

function formatDate(iso: string): string {
  const parsed = parseISO(iso);
  return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : iso;
}

/**
 * The membership cards the climber's gyms hold for them, read-only.
 *
 * Cards are entered at the desk and never by the person they cover, so there is
 * nothing to edit here. This exists so that somebody told "you are covered until
 * August" can check it without asking.
 *
 * The whole card disappears when there are none: an empty section on a settings
 * page reads as something broken rather than as something that does not apply.
 */
export default function MembershipCardsCard() {
  const { t } = useTranslation();
  const [cards, setCards] = useState<MembershipCard[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    membershipCardsService
      .mine()
      .then((mine) => {
        if (active) setCards(mine);
      })
      .catch((error) => {
        console.error('Failed to load membership cards:', error);
      })
      .finally(() => {
        if (active) setLoaded(true);
      });

    return () => {
      active = false;
    };
  }, []);

  if (!loaded || cards.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <IdCard className="h-5 w-5" />
          {t('memberships.title')}
        </CardTitle>
        <CardDescription>{t('memberships.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y">
          {cards.map((card) => (
            <li key={card.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{card.organization?.name}</span>
                  <span className="text-sm text-muted-foreground">{card.number}</span>
                  {card.kind && <Badge variant="outline">{card.kind}</Badge>}
                  {card.expired && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {t('memberships.expired')}
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {/* The issuing gym, or the roster holding a national card. */}
                  {card.location?.name ?? card.held_by?.name}
                </p>
              </div>
              <span className={card.expired ? 'text-sm text-destructive' : 'text-sm'}>
                {card.expires_on
                  ? t('memberships.expiresOn', { date: formatDate(card.expires_on) })
                  : t('memberships.noExpiry')}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
